import { z } from 'zod';

import { defineNode, total } from '../define';

export const EXPERIMENT_VARIABLES = [
  'hook',
  'visuals',
  'caption',
  'length',
  'format',
] as const;
export const WINNER_METRICS = ['watchTime', 'views', 'saves', 'shares'] as const;

export const splitTest = defineNode({
  type: 'experiment.splitTest',
  version: 1,
  category: 'experiment',
  label: 'Split test',
  description:
    'Change one variable — hook, visuals, caption, length or format — hold the rest.',
  icon: 'Flask',
  status: 'ga',
  inputs: [{ id: 'in', accepts: 'any' }],
  outputs: [{ id: 'out', type: 'same', variant: 'set' }],
  mode: 'map',
  executor: 'llm',
  config: z.object({
    variable: z.enum(EXPERIMENT_VARIABLES).default('hook').meta({
      title: 'Variable',
      widget: 'segmented',
    }),
    variants: z
      .int()
      .min(2)
      .max(6)
      .default(4)
      .meta({ title: 'Variants', widget: 'segmented' }),
  }),
  cardinality: (config, inputs) => ({ out: total(inputs) * config.variants }),
});

export const matrix = defineNode({
  type: 'experiment.matrix',
  version: 1,
  category: 'experiment',
  label: 'Matrix',
  description:
    'Cross two variables (e.g. 3 hooks × 2 visuals) with a cap on total renders.',
  icon: 'GridNine',
  status: 'ga',
  inputs: [{ id: 'in', accepts: 'any' }],
  outputs: [{ id: 'out', type: 'same', variant: 'set' }],
  mode: 'map',
  executor: 'llm',
  config: z
    .object({
      first: z
        .enum(EXPERIMENT_VARIABLES)
        .default('hook')
        .meta({ title: 'First variable' }),
      firstCount: z.int().min(2).max(5).default(3).meta({ title: 'Variants' }),
      second: z
        .enum(EXPERIMENT_VARIABLES)
        .default('visuals')
        .meta({ title: 'Second variable' }),
      secondCount: z.int().min(2).max(5).default(2).meta({ title: 'Variants' }),
      maxVariants: z.int().min(2).max(25).default(12).meta({ title: 'Cap' }),
    })
    .refine((config) => config.first !== config.second, {
      error: 'Pick two different variables',
      path: ['second'],
    }),
  cardinality: (config, inputs) => ({
    out:
      total(inputs) *
      Math.min(config.firstCount * config.secondCount, config.maxVariants),
  }),
});

export const pickWinner = defineNode({
  type: 'experiment.pickWinner',
  version: 1,
  category: 'experiment',
  label: 'Pick winner',
  description: 'After N hours, compare variants on a metric and promote the best.',
  icon: 'Trophy',
  status: 'ga',
  inputs: [{ id: 'in', accepts: 'any', variant: 'required' }],
  outputs: [{ id: 'out', type: 'source', variant: 'clear' }],
  mode: 'collect',
  executor: 'control',
  bounded: true,
  config: z.object({
    metric: z.enum(WINNER_METRICS).default('watchTime').meta({
      title: 'Compare on',
      widget: 'segmented',
    }),
    minViews: z.int().min(0).default(1000).meta({
      title: 'Minimum views per variant',
      description: 'Below this there is no clear winner',
    }),
    waitUpToHours: z.int().min(1).max(336).default(72).meta({
      title: 'Wait for views up to (h)',
      description: 'Checks hourly; past this, it reports no clear winner',
    }),
    then: z.enum(['stop', 'again']).default('stop').meta({
      title: 'When a winner is found',
      description: 'Again: run this flow once more with 5 variants like the winner',
      widget: 'segmented',
    }),
  }),
  cardinality: () => ({ out: 1 }),
});

export const EXPERIMENT = [splitTest, matrix, pickWinner];
